/**
 * publishStatusUi.tsx
 *
 * Shared presentation for a PublishRecord's status — the label, color, and the
 * pulsing dot for in-flight renders. Used by the Downloads tab and the Export
 * workflow's publishing/completed states so both read as one visual family.
 */

import { PublishStatus } from '@src/types.ts/publishRecord';
import { isPublishRecordInFlight } from '@src/utility/publishRecord';
import styled, { keyframes } from 'styled-components';

import type { PublishFingerprints } from '@src/types.ts/publishRecord';

export function formatPublishStatus(status: PublishStatus): string {
  switch (status) {
    case PublishStatus.Queued:
      return 'Queued';
    case PublishStatus.Rendering:
      return 'Publishing';
    case PublishStatus.Succeeded:
      return 'Succeeded';
    case PublishStatus.Failed:
      return 'Failed';
    case PublishStatus.RetryPending:
      return 'Retry Pending';
    default:
      return status;
  }
}

const statusColors: Record<PublishStatus, string> = {
  [PublishStatus.Succeeded]: 'var(--primary)',
  [PublishStatus.Rendering]: 'var(--accent)',
  [PublishStatus.Queued]: 'var(--accent)',
  [PublishStatus.RetryPending]: 'var(--text-muted)',
  [PublishStatus.Failed]: 'var(--destructive)',
};

export function PublishStatusBadge({ status }: { status: PublishStatus }) {
  return (
    <Badge $status={status}>
      {isPublishRecordInFlight(status) && <Pulse />}
      {formatPublishStatus(status)}
    </Badge>
  );
}

const OUTDATED_LABELS: Record<keyof PublishFingerprints, string> = {
  timeline: 'Timeline',
  transcript: 'Transcript',
  captions: 'Captions',
  translation: 'Translation',
  compositionPhrases: 'Overlay text',
  scenes: 'Scenes',
};

/**
 * Positive badge for a succeeded render that still reflects the current story
 * state (has a stored fingerprint and is not outdated). Green/`--primary` — the
 * reassuring "this is the up-to-date render" signal in the publish history.
 * `tooltipId` targets a shared `react-tooltip` instance rendered by the caller.
 */
export function CurrentBadge({ tooltipId }: { tooltipId?: string }) {
  return (
    <CurrentPill
      data-tooltip-id={tooltipId}
      data-tooltip-content="Up to date with the current clip"
    >
      Current
    </CurrentPill>
  );
}

/**
 * Neutral badge for a succeeded render whose source has changed since it was
 * published (`isOutdated`) — a previous version, not an error. Muted gray, so
 * a history full of superseded renders reads calmly instead of alarming.
 * `reasons` (from `whyOutdated`) name the changed categories in the hover
 * tooltip; `tooltipId` targets a shared `react-tooltip` instance rendered by
 * the caller.
 */
export function PreviousBadge({
  reasons,
  tooltipId,
}: {
  reasons: (keyof PublishFingerprints)[];
  tooltipId?: string;
}) {
  const labels = reasons.map(r => OUTDATED_LABELS[r]).filter(Boolean);
  const tooltip = labels.length
    ? `${labels.join(', ')} changed since this was published`
    : 'The source changed since this was published';
  return (
    <PreviousPill data-tooltip-id={tooltipId} data-tooltip-content={tooltip}>
      Previous
    </PreviousPill>
  );
}

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 var(--spacing-xs);
  border-radius: 4px;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.5;
  font-weight: var(--font-weight-medium);
  cursor: default;
`;

const CurrentPill = styled(Pill)`
  color: var(--primary);
  background: color-mix(in srgb, var(--primary) 14%, transparent);
`;

const PreviousPill = styled(Pill)`
  color: var(--text-secondary);
  background: color-mix(in srgb, var(--text-muted) 14%, transparent);
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const Pulse = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: ${pulseDot} 1.4s ease-in-out infinite;
`;

const Badge = styled.span<{ $status: PublishStatus }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: ${({ $status }) => statusColors[$status] ?? 'var(--text-muted)'};
`;
